/**
 * Per-shot camera guide overlay.
 *
 * Exterior shots (front / side / rear / 3-quarter) render a single CC0
 * low-poly SUV GLB (Quaternius, public domain via Poly Pizza) in WebGL as
 * edge lines — so every angle shares one consistent model with correct
 * perspective. Wheel / detail / interior variants stay hand-drawn since
 * those aren't car-silhouette overlays.
 */
import { Suspense, lazy } from 'react';

const Car3DGuide = lazy(() =>
  import('./Car3DGuide').then((m) => ({ default: m.Car3DGuide })),
);

export const THREE_D_GUIDES = new Set(['car-front', 'car-rear', 'car-side', 'car-3q']);

export function GuideOverlay({
  guide,
  shotKey,
  zoom,
}: {
  guide: string;
  shotKey: string;
  zoom?: number;
}) {
  return (
    <div className="pointer-events-none absolute inset-0">
      {/* Framing aids — rule-of-thirds + corner brackets (always on) */}
      <svg
        viewBox="0 0 200 150"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 h-full w-full text-cyan-400"
      >
        <g stroke="currentColor" strokeWidth="0.3" opacity="0.3" fill="none">
          <line x1="66.7" y1="14" x2="66.7" y2="136" strokeDasharray="1 1.8" />
          <line x1="133.3" y1="14" x2="133.3" y2="136" strokeDasharray="1 1.8" />
          <line x1="14" y1="50" x2="186" y2="50" strokeDasharray="1 1.8" />
          <line x1="14" y1="100" x2="186" y2="100" strokeDasharray="1 1.8" />
        </g>
        <g stroke="currentColor" strokeWidth="1" fill="none" opacity="0.85">
          <path d="M6 14 L6 6 L14 6" />
          <path d="M186 6 L194 6 L194 14" />
          <path d="M194 136 L194 144 L186 144" />
          <path d="M14 144 L6 144 L6 136" />
        </g>
      </svg>

      {/* Shot-specific silhouette */}
      {THREE_D_GUIDES.has(guide) ? (
        <Suspense fallback={null}>
          <Car3DGuide guide={guide} shotKey={shotKey} zoom={zoom} />
        </Suspense>
      ) : (
        <HandOverlay guide={guide} shotKey={shotKey} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2D hand-drawn variants for wheel/detail/interior shots (no 3D car needed).
// ---------------------------------------------------------------------------
function HandOverlay({ guide, shotKey }: { guide: string; shotKey: string }) {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ color: '#22d3ee', filter: 'drop-shadow(0 0 2px rgba(0,0,0,0.7))' }}
    >
      <svg viewBox="0 0 200 150" preserveAspectRatio="xMidYMid meet" className="h-[62%] w-[62%]">
        <g stroke="currentColor" fill="currentColor" fillOpacity="0.1"
           strokeLinejoin="round" strokeLinecap="round" opacity="0.9">
          {guide === 'wheel' && <WheelTarget />}
          {guide === 'detail' && <DetailTarget />}
          {guide === 'interior' && <InteriorGuide shotKey={shotKey} />}
        </g>
      </svg>
    </div>
  );
}

function WheelTarget() {
  // Wheel is the focal point but not the only thing: include the quarter
  // panel / fender above the wheel and a ground strip below so the photo
  // reads as a contextual dealership shot, not a rim catalog entry.
  const cx = 100;
  const cy = 92;
  const r = 34;
  return (
    <g>
      {/* Upper body / quarter panel — subtle rectangle above the fender arch */}
      <path
        strokeWidth="1.2"
        fillOpacity="0.08"
        d="M 12 28 L 188 28 L 188 72 L 142 72 A 42 42 0 0 0 58 72 L 12 72 Z"
      />
      {/* Body character line (horizontal crease across panel) */}
      <line x1="18" y1="50" x2="182" y2="50" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.55" />
      {/* Door seam hint */}
      <line x1="150" y1="30" x2="150" y2="70" strokeWidth="0.5" strokeDasharray="1.5 1.5" opacity="0.5" />

      {/* Wheel arch (fender opening over the wheel) */}
      <path d="M 58 72 A 42 42 0 0 1 142 72" strokeWidth="1.5" fill="none" />

      {/* Tire */}
      <circle cx={cx} cy={cy} r={r} strokeWidth="1.8" fill="#0b1220" fillOpacity="0.5" />
      {/* Rim lip */}
      <circle cx={cx} cy={cy} r="25" strokeWidth="1.2" fillOpacity="0.12" />
      {/* Center cap */}
      <circle cx={cx} cy={cy} r="5" strokeWidth="1" fillOpacity="0.25" />
      <circle cx={cx} cy={cy} r="1.6" fill="currentColor" stroke="none" />
      {/* 5 spokes */}
      {[0, 72, 144, 216, 288].map((a) => {
        const rad = ((a - 90) * Math.PI) / 180;
        return (
          <line key={a}
            x1={cx + 5 * Math.cos(rad)} y1={cy + 5 * Math.sin(rad)}
            x2={cx + 23 * Math.cos(rad)} y2={cy + 23 * Math.sin(rad)}
            strokeWidth="2.4"
          />
        );
      })}

      {/* Ground strip under the wheel */}
      <line x1="8" y1="128" x2="192" y2="128" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.55" />
      <rect x="8" y="128" width="184" height="10" fillOpacity="0.06" strokeWidth="0" />

      <text x={cx} y="146" textAnchor="middle" fontSize="5.5" fill="currentColor" stroke="none" fontWeight="600">
        Wheel + fender
      </text>
    </g>
  );
}

function DetailTarget() {
  return (
    <g>
      <rect x="74" y="54" width="52" height="36" rx="5" strokeWidth="1.3" fillOpacity="0.08" />
      <line x1="100" y1="68" x2="100" y2="76" strokeWidth="0.9" />
      <line x1="96" y1="72" x2="104" y2="72" strokeWidth="0.9" />
      {[
        [74, 54, 82, 54, 74, 62],
        [126, 54, 118, 54, 126, 62],
        [74, 90, 82, 90, 74, 82],
        [126, 90, 118, 90, 126, 82],
      ].map(([x1, y1, x2, y2, x3, y3], i) => (
        <path key={i} strokeWidth="1.4" d={`M ${x2} ${y2} L ${x1} ${y1} L ${x3} ${y3}`} fill="none" />
      ))}
      <text x="100" y="110" textAnchor="middle" fontSize="6" fill="currentColor" stroke="none" fontWeight="600">
        Center &amp; fill
      </text>
    </g>
  );
}

function InteriorGuide({ shotKey }: { shotKey: string }) {
  if (shotKey === 'dash') {
    return (
      <g>
        <path strokeWidth="1.4" fillOpacity="0.1" d="M 50 58 Q 100 44, 150 58 L 152 76 L 48 76 Z" />
        <circle cx="78" cy="96" r="14" strokeWidth="1.6" fillOpacity="0.15" />
        <circle cx="78" cy="96" r="3" fill="currentColor" fillOpacity="0.6" stroke="none" />
        {[90, 210, 330].map((a) => {
          const rad = ((a - 90) * Math.PI) / 180;
          return <line key={a} x1={78 + 3 * Math.cos(rad)} y1={96 + 3 * Math.sin(rad)} x2={78 + 13 * Math.cos(rad)} y2={96 + 13 * Math.sin(rad)} strokeWidth="1.4" />;
        })}
        <rect x="108" y="84" width="32" height="24" rx="3" strokeWidth="1.2" fillOpacity="0.12" />
        <rect x="112" y="88" width="24" height="10" rx="1.2" strokeWidth="0.7" fillOpacity="0.25" />
      </g>
    );
  }
  if (shotKey === 'cluster') {
    return (
      <g>
        <circle cx="100" cy="92" r="40" strokeWidth="0.7" strokeDasharray="2 2" opacity="0.45" fill="none" />
        <rect x="74" y="50" width="52" height="30" rx="5" strokeWidth="1.5" fillOpacity="0.15" />
        <circle cx="86" cy="65" r="9" strokeWidth="0.9" />
        <circle cx="114" cy="65" r="9" strokeWidth="0.9" />
        <line x1="86" y1="65" x2="90" y2="60" strokeWidth="1.1" />
        <line x1="114" y1="65" x2="118" y2="60" strokeWidth="1.1" />
        <text x="100" y="130" textAnchor="middle" fontSize="5.5" fill="currentColor" stroke="none" fontWeight="600">
          Center behind wheel
        </text>
      </g>
    );
  }
  if (shotKey === 'infotainment') {
    return (
      <g>
        <rect x="58" y="48" width="84" height="54" rx="6" strokeWidth="1.5" fillOpacity="0.1" />
        <rect x="64" y="54" width="72" height="42" rx="2" strokeWidth="0.7" strokeDasharray="1.5 1.5" fillOpacity="0.12" />
        {[0, 1, 2].map((c) => (
          <rect key={`r0-${c}`} x={70 + c * 22} y="60" width="16" height="14" rx="2" strokeWidth="0.5" opacity="0.7" />
        ))}
        {[0, 1, 2].map((c) => (
          <rect key={`r1-${c}`} x={70 + c * 22} y="78" width="16" height="14" rx="2" strokeWidth="0.5" opacity="0.7" />
        ))}
      </g>
    );
  }
  if (shotKey === 'seat_driver') {
    // Single bucket seat — pronounced bolsters, headrest, seat bottom.
    return (
      <g>
        {/* Headrest */}
        <rect x="84" y="20" width="32" height="14" rx="5" strokeWidth="1.4" fillOpacity="0.22" />
        {/* Headrest-to-backrest neck */}
        <rect x="96" y="32" width="8" height="6" strokeWidth="0.7" fillOpacity="0.15" />
        {/* Backrest — tall with shoulder contour + side bolsters */}
        <path
          strokeWidth="1.6"
          fillOpacity="0.14"
          d="
            M 76 40
            C 76 36, 80 34, 84 34
            L 116 34
            C 120 34, 124 36, 124 40
            L 126 70
            C 126 76, 122 80, 118 80
            L 82 80
            C 78 80, 74 76, 74 70
            Z"
        />
        {/* Bolster creases (backrest) */}
        <path strokeWidth="0.7" fill="none" opacity="0.55"
          d="M 86 40 C 88 54, 88 70, 86 78" />
        <path strokeWidth="0.7" fill="none" opacity="0.55"
          d="M 114 40 C 112 54, 112 70, 114 78" />
        {/* Gap between backrest and cushion */}
        <line x1="76" y1="82" x2="124" y2="82" strokeWidth="0.5" strokeDasharray="1 1" opacity="0.5" />
        {/* Seat cushion — wider, shorter, with forward bolsters */}
        <path
          strokeWidth="1.6"
          fillOpacity="0.14"
          d="
            M 72 84
            C 68 84, 66 88, 66 92
            L 66 104
            C 66 110, 70 114, 76 114
            L 124 114
            C 130 114, 134 110, 134 104
            L 134 92
            C 134 88, 132 84, 128 84
            Z"
        />
        {/* Cushion bolster creases */}
        <path strokeWidth="0.7" fill="none" opacity="0.55"
          d="M 78 86 C 76 94, 76 106, 78 112" />
        <path strokeWidth="0.7" fill="none" opacity="0.55"
          d="M 122 86 C 124 94, 124 106, 122 112" />
        {/* Center stitching */}
        <line x1="100" y1="40" x2="100" y2="80" strokeWidth="0.5" strokeDasharray="2 1.5" opacity="0.7" />
        <line x1="100" y1="86" x2="100" y2="112" strokeWidth="0.5" strokeDasharray="2 1.5" opacity="0.7" />
      </g>
    );
  }
  if (shotKey === 'seat_rear') {
    // Three-passenger bench — three headrests in a row, long backrest + bottom.
    return (
      <g>
        {/* Three headrests */}
        <rect x="38"  y="24" width="28" height="13" rx="5" strokeWidth="1.3" fillOpacity="0.22" />
        <rect x="86"  y="24" width="28" height="13" rx="5" strokeWidth="1.3" fillOpacity="0.22" />
        <rect x="134" y="24" width="28" height="13" rx="5" strokeWidth="1.3" fillOpacity="0.22" />
        {/* Neck stems where each headrest plugs into the backrest */}
        {[52, 100, 148].map((x) => (
          <rect key={x} x={x - 4} y="35" width="8" height="6" strokeWidth="0.6" fillOpacity="0.15" />
        ))}
        {/* Long backrest */}
        <path
          strokeWidth="1.6"
          fillOpacity="0.14"
          d="
            M 30 42
            C 30 38, 32 36, 36 36
            L 164 36
            C 168 36, 170 38, 170 42
            L 172 74
            C 172 78, 170 80, 166 80
            L 34 80
            C 30 80, 28 78, 28 74
            Z"
        />
        {/* Seat dividers on backrest (2 vertical seams making 3 sections) */}
        <line x1="76"  y1="40" x2="76"  y2="78" strokeWidth="0.7" strokeDasharray="1.8 1.5" opacity="0.7" />
        <line x1="124" y1="40" x2="124" y2="78" strokeWidth="0.7" strokeDasharray="1.8 1.5" opacity="0.7" />
        {/* Gap between backrest and bench cushion */}
        <line x1="30" y1="82" x2="170" y2="82" strokeWidth="0.5" strokeDasharray="1 1" opacity="0.5" />
        {/* Long bench cushion */}
        <path
          strokeWidth="1.6"
          fillOpacity="0.14"
          d="
            M 28 84
            C 24 84, 22 88, 22 92
            L 22 106
            C 22 110, 24 112, 28 112
            L 172 112
            C 176 112, 178 110, 178 106
            L 178 92
            C 178 88, 176 84, 172 84
            Z"
        />
        {/* Seat dividers on cushion */}
        <line x1="76"  y1="86" x2="76"  y2="110" strokeWidth="0.7" strokeDasharray="1.8 1.5" opacity="0.7" />
        <line x1="124" y1="86" x2="124" y2="110" strokeWidth="0.7" strokeDasharray="1.8 1.5" opacity="0.7" />
        {/* Subtle center-seat armrest hint (folded up) */}
        <rect x="94" y="50" width="12" height="14" rx="1.5" strokeWidth="0.5" fillOpacity="0.1" opacity="0.6" />
      </g>
    );
  }
  if (shotKey === 'cargo') {
    return (
      <g>
        <path strokeWidth="1.5" fillOpacity="0.1" d="M 56 44 L 144 44 L 166 110 L 34 110 Z" />
        <line x1="78" y1="44" x2="66" y2="110" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.6" />
        <line x1="122" y1="44" x2="134" y2="110" strokeWidth="0.6" strokeDasharray="2 2" opacity="0.6" />
      </g>
    );
  }
  if (shotKey === 'odometer') {
    return (
      <g>
        <rect x="60" y="48" width="80" height="44" rx="4" strokeWidth="1.4" fillOpacity="0.1" />
        <circle cx="100" cy="70" r="16" strokeWidth="0.8" strokeDasharray="2 2" />
        <rect x="86" y="72" width="28" height="10" rx="1.2" strokeWidth="1.4" fillOpacity="0.25" />
        <text x="100" y="112" textAnchor="middle" fontSize="5.5" fill="currentColor" stroke="none" fontWeight="600">
          Fill with cluster
        </text>
      </g>
    );
  }
  if (shotKey === 'engine') {
    return (
      <g>
        <rect x="40" y="40" width="120" height="70" rx="6" strokeWidth="1.5" fillOpacity="0.1" />
        <rect x="72" y="58" width="56" height="40" rx="4" strokeWidth="1.1" fillOpacity="0.15" />
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1={80 + i * 14} y1="58" x2={80 + i * 14} y2="98" strokeWidth="0.5" />
        ))}
      </g>
    );
  }
  return <rect x="50" y="44" width="100" height="62" rx="6" strokeWidth="1.4" fillOpacity="0.08" />;
}
